"use client"

import {
  Building2,
  GraduationCap,
  Lightbulb,
  TrendingUp,
  Users,
  Briefcase,
  Globe,
  Target,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useRef } from "react"
import { Button } from "@/components/ui/button"
import Link from "next/link"

export function WhoShouldAttend() {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const attendees = [
    {
      icon: Building2,
      title: "Ministers & Policymakers",
      description: "Government officials and multilateral organizations shaping education and digital economy policies",
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
    },
    {
      icon: GraduationCap,
      title: "Global Universities",
      description: "International institutions seeking partnerships and student recruitment opportunities in Africa",
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
    },
    {
      icon: Lightbulb,
      title: "EdTech Innovators",
      description: "Technology companies and startups revolutionizing education delivery and access",
      color: "text-secondary",
      bgColor: "bg-secondary/10",
    },
    {
      icon: TrendingUp,
      title: "Investors & VCs",
      description: "Venture capital firms and angel investors looking for high-impact education opportunities",
      color: "text-green-500",
      bgColor: "bg-green-500/10",
    },
    {
      icon: Briefcase,
      title: "Development Finance",
      description: "DFIs and impact investors funding sustainable education and workforce development",
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10",
    },
    {
      icon: Users,
      title: "Employers & Recruiters",
      description: "Companies seeking to connect with Africa's emerging talent pool and future workforce",
      color: "text-pink-500",
      bgColor: "bg-pink-500/10",
    },
    {
      icon: Globe,
      title: "International Organizations",
      description: "NGOs and multilateral bodies working on education access and quality improvement",
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
    },
    {
      icon: Target,
      title: "Students & Youth Leaders",
      description: "2,000+ African students and emerging leaders ready to shape the continent's future",
      color: "text-secondary",
      bgColor: "bg-secondary/10",
    },
  ]

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft + (direction === "left" ? -scrollAmount : scrollAmount)
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      })
    }
  }

  return (
    <section id="who-should-attend" className="bg-muted py-20 md:py-28">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl text-balance">
            Who Should Attend?
          </h2>
          <p className="text-lg text-muted-foreground text-pretty">
            A solutions marketplace and deal-making arena bringing together Africa's most influential education and
            digital economy stakeholders
          </p>
        </div>

        <div className="relative">
          {/* Navigation Buttons */}
          <Button
            variant="outline"
            size="icon"
            className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-background shadow-lg hover:bg-accent"
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full bg-background shadow-lg hover:bg-accent"
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          <div
            ref={scrollContainerRef}
            className="grid grid-rows-2 auto-cols-[300px] grid-flow-col gap-6 overflow-x-auto px-12 pb-4 scrollbar-hide snap-x snap-mandatory"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {attendees.map((attendee, index) => {
              const Icon = attendee.icon
              return (
                <div
                  key={index}
                  className="group relative snap-start overflow-hidden rounded-xl border border-border bg-card p-6 transition-all hover:shadow-lg hover:border-secondary"
                >
                  {/* Icon Container */}
                  <div className={`mb-4 inline-flex rounded-lg ${attendee.bgColor} p-3`}>
                    <Icon className={`h-6 w-6 ${attendee.color}`} />
                  </div>

                  {/* Content */}
                  <h3 className="mb-2 text-lg font-semibold text-card-foreground text-balance">{attendee.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground text-pretty">{attendee.description}</p>

                  {/* Hover Effect Border */}
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-secondary to-primary scale-x-0 transition-transform group-hover:scale-x-100" />
                </div>
              )
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <p className="text-base text-muted-foreground mb-4">
            Join the conversation that will shape the future of African education and digital economy
          </p>
          <Link
            href="/register"
            className="inline-flex items-center gap-2 rounded-lg bg-secondary px-8 py-3 font-semibold text-secondary-foreground transition-all hover:bg-secondary/90 hover:shadow-lg"
          >
            Register Now
          </Link>
        </div>
      </div>
    </section>
  )
}
